import React from "react"
import { KBarProvider, KBarPortal, useKBar } from "kbar"
import { ErrorBoundary } from "react-error-boundary"
import styled from "styled-components"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import { StyledPositioner, StyledAnimator, StyledSearch } from "./styles"
import { CurrentPageHeadings } from "./components/CurrentPageHeadings"
import { RecentlyViewedDocs } from "./components/RecentlyViewedDocs"
import { AlgoliaSearchIntegration } from "./components/AlgoliaSearchIntegration"
import { RenderResults } from "./components/RenderResults"
import { useCurrentRepo } from "./hooks/useCurrentRepo"

const SearchHeader = styled.div`
  position: relative;
  border-bottom: 1px solid ${uiColorToBaseColor("lighterGray")};
`

const ScopeIndicator = styled.div`
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 600;
  color: ${uiColorToBaseColor("gray")};
  background: ${uiColorToBaseColor("lightestGray")};
  border-bottom: 1px solid ${uiColorToBaseColor("lighterGray")};
  display: flex;
  align-items: center;
  gap: 6px;
`

const EscButton = styled.button`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  background: transparent;
  border: 1px solid ${uiColorToBaseColor("lightGray")};
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
  font-family: monospace;
  color: ${uiColorToBaseColor("gray")};
  transition: all 0.2s ease;

  &:hover {
    background: ${uiColorToBaseColor("lightestGray")};
    border-color: ${uiColorToBaseColor("darkGray")};
    color: ${uiColorToBaseColor("darkGray")};
  }
`

function SearchWithEsc() {
  const { query } = useKBar()
  const currentRepo = useCurrentRepo()

  return (
    <>
      {!currentRepo && (
        <ScopeIndicator role="status" aria-live="polite">
          <span role="img" aria-label="global search">
            🌐
          </span>
          {" "}Search all documentation
        </ScopeIndicator>
      )}
      <SearchHeader>
        <StyledSearch placeholder="Search documentation..." />
        <EscButton onClick={() => query.toggle()} aria-label="Close search">
          esc
        </EscButton>
      </SearchHeader>
    </>
  )
}

function KBarErrorFallback() {
  return null
}

interface Props {
  children: React.ReactNode
}

export default function KBarProviderWrapper({ children }: Props) {
  const defaultActions = []

  return (
    <ErrorBoundary
      FallbackComponent={KBarErrorFallback}
      onError={(error, errorInfo) => {
        console.error("kbar error:", error, errorInfo)
      }}
    >
      <KBarProvider actions={defaultActions}>
        <KBarPortal>
          <CurrentPageHeadings />
          <RecentlyViewedDocs />
          <AlgoliaSearchIntegration />
          <StyledPositioner>
            <StyledAnimator>
              <SearchWithEsc />
              <RenderResults />
            </StyledAnimator>
          </StyledPositioner>
        </KBarPortal>
        {children}
      </KBarProvider>
    </ErrorBoundary>
  )
}
